/**
 * Notifications hooks (FND-008, ADH-009).
 *
 * Wraps the Notifications service. Shapes verified against
 * services/notifications/schemas.py + routes.py:
 *   - GET /api/v1/notifications/deliveries → { items: DeliveryEvent[] } (delivery / audit trail)
 *   - GET /api/v1/notifications/templates  → { items: Template[] }      (message templates)
 *
 * Email-template usage stats (ADH-009) are read from `GET /admin/email-templates`
 * when the sibling worker exposes it, otherwise derived from the templates list +
 * delivery counts so the admin Email Templates tab still shows usage. Each fetch
 * tolerates a bare array / `{ items }` envelope and degrades on 404.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** A delivery / audit-trail event (notifications DeliveryEvent DTO). */
export interface DeliveryEvent {
  event_id: string;
  tenant_id: string;
  message_id: string;
  /** email | sms | push | inbox. */
  channel: string;
  to: string;
  template_id: string;
  /** queued | sent | delivered | opened | bounced | failed | suppressed_tcpa. */
  status: string;
  reason?: string | null;
  patient_id?: string | null;
  case_id?: string | null;
  occurred_at: string;
}

/** A message template (notifications Template DTO). */
export interface MessageTemplate {
  template_id: string;
  tenant_id: string;
  channel: string;
  name: string;
  subject?: string | null;
  body: string;
  variables: string[];
  version: number;
  state?: string | null;
  /** Derived/served usage count (ADH-009). */
  usage_count?: number | null;
}

function unwrap<T>(data: T[] | { items?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? [];
}

interface UseNotificationsResult {
  deliveries: DeliveryEvent[];
  templates: MessageTemplate[];
  deliveriesAvailable: boolean;
  templatesAvailable: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

export function useNotifications(): UseNotificationsResult {
  const [deliveries, setDeliveries] = useState<DeliveryEvent[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [deliveriesAvailable, setDeliveriesAvailable] = useState(true);
  const [templatesAvailable, setTemplatesAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const load = <T>(
      path: string,
      setRows: (rows: T[]) => void,
      setAvailable: (a: boolean) => void
    ) =>
      apiClient
        .get<T[] | { items?: T[] }>(path)
        .then((data) => {
          setRows(unwrap(data));
          setAvailable(true);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            setRows([]);
            setAvailable(false);
            return;
          }
          throw err;
        });

    const ps = [
      load<DeliveryEvent>('/notifications/deliveries', setDeliveries, setDeliveriesAvailable),
      load<MessageTemplate>('/notifications/templates', setTemplates, setTemplatesAvailable),
    ];

    Promise.allSettled(ps)
      .then((results) => {
        const failure = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    deliveries,
    templates,
    deliveriesAvailable,
    templatesAvailable,
    isLoading,
    error,
    refresh,
  };
}
